"use client";

import React, { useEffect, useState } from 'react';
import { MoreHorizontal, Plus, Clock, DollarSign, X, Edit2, Trash2, Save, UserPlus, Mail, Phone, Target, Users, CheckCircle2 } from 'lucide-react';
import { supabase } from '../../integrations/supabase/client';
import { useNotifications } from '../../contexts/NotificationContext';
import { logActivity } from '../../utils/activityLogger';

interface Lead {
  id: string;
  name: string;
  email: string;
  phone?: string;
  media_name: string;
  plan_interest: string;
  status: string;
  priority: string;
  value?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface PipelineData {
  new: Lead[];
  contacted: Lead[];
  qualified: Lead[];
  closed: Lead[];
}

const PipelineColumn = ({ 
  title, 
  leads, 
  status, 
  onStatusChange, 
  onEditLead, 
  onDeleteLead,
  onConvertToClient,
  onPriorityChange
}: {
  title: string;
  leads: Lead[];
  status: string;
  onStatusChange: (leadId: string, newStatus: string) => void;
  onEditLead: (lead: Lead) => void;
  onDeleteLead: (leadId: string) => void;
  onConvertToClient: (lead: Lead) => void;
  onPriorityChange: (leadId: string, newPriority: string) => void;
}) => {
  const [draggedLead, setDraggedLead] = useState<Lead | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, lead: Lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', lead.id);
    // Add visual feedback
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    // Reset visual feedback
    e.currentTarget.style.opacity = '1';
    setDraggedLead(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStatus(status);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only reset if leaving the column itself
    if (e.currentTarget === e.target) {
      setDragOverStatus(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverStatus(null);
    if (draggedLead && draggedLead.status !== status) {
      onStatusChange(draggedLead.id, status);
    }
    setDraggedLead(null);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'alta': return 'bg-red-50 text-red-600';
      case 'media': return 'bg-blue-50 text-blue-600';
      case 'baja': return 'bg-gray-50 text-gray-600';
      default: return 'bg-gray-50 text-gray-600';
    }
  };

  return (
    <div className="flex flex-col gap-4 min-w-[300px]">
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-charcoal">{title}</h3>
          <span className="bg-gray-100 text-charcoal-muted text-[10px] font-bold px-2 py-0.5 rounded-full">
            {leads.length}
          </span>
        </div>
      </div>
      
      <div 
        className={`flex flex-col gap-3 min-h-[200px] p-2 rounded-2xl border-2 transition-all ${
          dragOverStatus === status 
            ? 'border-primary bg-blue-50 scale-[1.02]' 
            : draggedLead 
            ? 'border-gray-300 bg-gray-50' 
            : 'border-dashed border-gray-200'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {leads.map((lead) => (
          <div
            key={lead.id}
            draggable
            onDragStart={(e) => handleDragStart(e, lead)}
            onDragEnd={handleDragEnd}
            className="bg-white p-5 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing animate-slide-up"
          >
            <div className="flex justify-between items-start mb-3">
              <select
                value={lead.priority}
                onChange={(e) => {
                  // Update priority directly
                  onPriorityChange(lead.id, e.target.value);
                }}
                className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border-0 cursor-pointer ${getPriorityColor(lead.priority)}`}
                onClick={(e) => e.stopPropagation()}
              >
                <option value="baja">Baja</option>
                <option value="media">Media</option>
                <option value="alta">Alta</option>
              </select>
              <div className="flex items-center gap-1">
                {status === 'qualified' && (
                  <button 
                    onClick={() => onConvertToClient(lead)}
                    className="p-1 hover:bg-green-50 rounded-lg transition-colors"
                    title="Convertir a Cliente"
                  >
                    <UserPlus size={14} className="text-green-500" />
                  </button>
                )}
                <button 
                  onClick={() => onEditLead(lead)}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Edit2 size={14} className="text-gray-400" />
                </button>
                <button 
                  onClick={() => onDeleteLead(lead.id)}
                  className="p-1 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={14} className="text-red-400" />
                </button>
              </div>
            </div>
            
            <h4 className="font-bold text-charcoal mb-1">{lead.media_name || 'Sin medio'}</h4>
            <p className="text-xs text-charcoal-muted mb-1">{lead.plan_interest || 'Sin servicio'}</p>
            <p className="text-xs text-charcoal-muted mb-2">{lead.name}</p>
            <div className="space-y-1 mb-4">
              <div className="flex items-center gap-1 text-xs text-charcoal-muted">
                <Mail size={10} /> {lead.email}
              </div>
              {lead.phone && (
                <div className="flex items-center gap-1 text-xs text-charcoal-muted">
                  <Phone size={10} /> {lead.phone}
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t border-gray-50">
              <div className="flex items-center gap-1 text-primary font-bold text-xs">
                <DollarSign size={12} /> {lead.value ? `$${lead.value.toLocaleString()}` : 'Sin valor'}
              </div>
              <div className="flex items-center gap-1 text-charcoal-muted text-[10px] font-medium">
                <Clock size={12} /> {Math.floor((new Date().getTime() - new Date(lead.created_at).getTime()) / (1000 * 60 * 60 * 24))}d
              </div>
            </div>
          </div>
        ))}
        
        {leads.length === 0 && (
          <div className="text-center py-8 text-charcoal-muted">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Plus size={20} className="text-gray-400" />
            </div>
            <p className="text-xs">Arrastra leads aquí</p>
            {draggedLead && (
              <p className="text-xs text-primary font-medium mt-2">Suelta para mover</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Pipeline = () => {
  const [pipelineData, setPipelineData] = useState<PipelineData>({
    new: [],
    contacted: [],
    qualified: [],
    closed: []
  });
  const [loading, setLoading] = useState(true);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const { addNotification } = useNotifications();

  useEffect(() => {
    fetchPipelineData();
    
    // Set up real-time subscription
    const subscription = supabase
      .channel('pipeline-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'leads' },
        (payload) => {
          fetchPipelineData();
          // Optional: Add notification for real-time updates
          if (payload.eventType === 'INSERT') {
            // Could trigger notification here
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchPipelineData = async () => {
    try {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const grouped = data?.reduce((acc, lead) => {
        acc[lead.status as keyof PipelineData] = [...(acc[lead.status as keyof PipelineData] || []), lead];
        return acc;
      }, { new: [], contacted: [], qualified: [], closed: [] } as PipelineData);

      setPipelineData(grouped || { new: [], contacted: [], qualified: [], closed: [] });
    } catch (error) {
      console.error('Error fetching pipeline data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLead = async (lead: Lead) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({
          name: lead.name,
          email: lead.email,
          phone: lead.phone,
          media_name: lead.media_name,
          plan_interest: lead.plan_interest,
          priority: lead.priority,
          value: lead.value,
          notes: lead.notes
        })
        .eq('id', lead.id);

      if (error) throw error;
      setEditingLead(null);
    } catch (error) {
      console.error('Error saving lead:', error);
    }
  };

  const handlePriorityChange = async (leadId: string, newPriority: string) => {
    try {
      const { data: oldLead } = await supabase
        .from('leads')
        .select('*')
        .eq('id', leadId)
        .single();

      const { error } = await supabase
        .from('leads')
        .update({ priority: newPriority })
        .eq('id', leadId);

      if (error) throw error;

      // Log activity
      await logActivity('updated', 'leads', leadId, oldLead, { priority: newPriority });
      
      // Show notification
      addNotification({
        type: 'success',
        title: 'Prioridad actualizada',
        message: `Cambiada a ${newPriority}`
      });
    } catch (error) {
      console.error('Error updating lead priority:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudo actualizar la prioridad'
      });
    }
  };

  const handleStatusChange = async (leadId: string, newStatus: string) => {
    try {
      const { data: oldLead } = await supabase
        .from('leads')
        .select('*')
        .eq('id', leadId)
        .single();

      const { error } = await supabase
        .from('leads')
        .update({ status: newStatus })
        .eq('id', leadId);

      if (error) throw error;

      // Log activity
      await logActivity('updated', 'leads', leadId, oldLead, { status: newStatus });
      
      // Show notification
      addNotification({
        type: 'success',
        title: 'Lead actualizado',
        message: `Estado cambiado a ${newStatus}`
      });
    } catch (error) {
      console.error('Error updating lead status:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudo actualizar el lead'
      });
    }
  };

  const handleDeleteLead = async (leadId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este lead?')) return;
    
    try {
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', leadId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  const handleConvertToClient = async (lead: Lead) => {
    if (!confirm(`¿Convertir ${lead.name} a cliente?`)) return;
    
    try {
      // Create client from lead
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .insert({
          name: lead.name,
          email: lead.email,
          phone: lead.phone,
          media_name: lead.media_name,
          contact_person: lead.name,
          tier: 'Mediano', // Default tier
          status: 'active'
        })
        .select()
        .single();

      if (clientError) throw clientError;

      // Update lead status to closed
      const { error: leadError } = await supabase
        .from('leads')
        .update({ status: 'closed' })
        .eq('id', lead.id);

      if (leadError) throw leadError;

      // Log activity
      await logActivity('converted', 'leads', lead.id, lead, { status: 'closed', client_id: client?.id });
      await logActivity('created', 'clients', client?.id, {}, client);
      
      // Show notification
      addNotification({
        type: 'success',
        title: 'Lead convertido',
        message: `${lead.name} ahora es un cliente`
      });
    } catch (error) {
      console.error('Error converting lead to client:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudo convertir el lead a cliente'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-charcoal">Pipeline de Ventas</h1>
          <p className="text-charcoal-muted">Gestiona el flujo de leads desde captación hasta cierre.</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn-primary"
        >
          <Plus size={20} /> Nuevo Lead
        </button>
      </div>

      {/* Pipeline Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-2xl border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
              <Target size={16} className="text-gray-600" />
            </div>
            <span className="text-sm font-medium text-charcoal-muted">Nuevos</span>
          </div>
          <p className="text-2xl font-bold text-charcoal">{pipelineData.new.length}</p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users size={16} className="text-blue-600" />
            </div>
            <span className="text-sm font-medium text-charcoal-muted">Contactados</span>
          </div>
          <p className="text-2xl font-bold text-charcoal">{pipelineData.contacted.length}</p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <CheckCircle2 size={16} className="text-purple-600" />
            </div>
            <span className="text-sm font-medium text-charcoal-muted">Calificados</span>
          </div>
          <p className="text-2xl font-bold text-charcoal">{pipelineData.qualified.length}</p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign size={16} className="text-green-600" />
            </div>
            <span className="text-sm font-medium text-charcoal-muted">Cerrados</span>
          </div>
          <p className="text-2xl font-bold text-charcoal">{pipelineData.closed.length}</p>
        </div>
      </div>

      <div className="flex gap-6 overflow-x-auto pb-4">
        <PipelineColumn
          title="Nuevos"
          leads={pipelineData.new}
          status="new"
          onStatusChange={handleStatusChange}
          onEditLead={setEditingLead}
          onDeleteLead={handleDeleteLead}
          onConvertToClient={handleConvertToClient}
          onPriorityChange={handlePriorityChange}
        />
        <PipelineColumn
          title="Contactados"
          leads={pipelineData.contacted}
          status="contacted"
          onStatusChange={handleStatusChange}
          onEditLead={setEditingLead}
          onDeleteLead={handleDeleteLead}
          onConvertToClient={handleConvertToClient}
          onPriorityChange={handlePriorityChange}
        />
        <PipelineColumn
          title="Calificados"
          leads={pipelineData.qualified}
          status="qualified"
          onStatusChange={handleStatusChange}
          onEditLead={setEditingLead}
          onDeleteLead={handleDeleteLead}
          onConvertToClient={handleConvertToClient}
          onPriorityChange={handlePriorityChange}
        />
        <PipelineColumn
          title="Cerrados"
          leads={pipelineData.closed}
          status="closed"
          onStatusChange={handleStatusChange}
          onEditLead={setEditingLead}
          onDeleteLead={handleDeleteLead}
          onConvertToClient={handleConvertToClient}
          onPriorityChange={handlePriorityChange}
        />
      </div>

      {/* Edit Lead Modal */}
      {editingLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in my-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-charcoal">Editar Lead</h2>
              <button 
                onClick={() => setEditingLead(null)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Nombre</label>
                <input
                  type="text"
                  value={editingLead.name}
                  onChange={(e) => setEditingLead({...editingLead, name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Email</label>
                <input
                  type="email"
                  value={editingLead.email}
                  onChange={(e) => setEditingLead({...editingLead, email: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Teléfono</label>
                <input
                  type="tel"
                  value={editingLead.phone || ''}
                  onChange={(e) => setEditingLead({...editingLead, phone: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Medio</label>
                <input
                  type="text"
                  value={editingLead.media_name}
                  onChange={(e) => setEditingLead({...editingLead, media_name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Servicio</label>
                <input
                  type="text"
                  value={editingLead.plan_interest}
                  onChange={(e) => setEditingLead({...editingLead, plan_interest: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Prioridad</label>
                <select
                  value={editingLead.priority}
                  onChange={(e) => setEditingLead({...editingLead, priority: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="baja">Baja</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Valor</label>
                <input
                  type="number"
                  value={editingLead.value || ''}
                  onChange={(e) => setEditingLead({...editingLead, value: Number(e.target.value)})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-charcoal mb-2">Notas</label>
                <textarea
                  value={editingLead.notes || ''}
                  onChange={(e) => setEditingLead({...editingLead, notes: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setEditingLead(null)}
                className="flex-1 px-6 py-3 border border-gray-200 text-charcoal rounded-2xl font-bold hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleSaveLead(editingLead)}
                className="flex-1 btn-primary"
              >
                <Save size={20} /> Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pipeline;